/** Каталог: слева дерево, справа таблица книг, снизу карточка выбранной. */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, coverUrl, downloadUrl, formatSize } from '../api'
import type {
  AppInfo,
  BookDetail,
  BookDetails,
  BookFilter,
  BookSummary,
  ShelfBook,
} from '../api'
import { useAuth } from '../auth'
import { BookTable } from '../components/BookTable'
import { CatalogTree } from '../components/CatalogTree'
import { Spinner } from '../components/Spinner'

const PAGE = 100

export function CatalogPage() {
  const { username, logout } = useAuth()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<BookFilter>({})
  const [scope, setScope] = useState('')
  const [sort, setSort] = useState('title')
  const [readable, setReadable] = useState(false)

  const [books, setBooks] = useState<BookSummary[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [selected, setSelected] = useState<BookDetail | null>(null)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [app, setApp] = useState<AppInfo | null>(null)
  const [details, setDetails] = useState<Record<string, BookDetails>>({})
  // На узком экране дерево не помещается рядом с таблицей и открывается
  // поверх неё по кнопке.
  const [treeOpen, setTreeOpen] = useState(false)
  // Подсказка, когда поиск ничего не дал: возможно, опечатка в фамилии.
  const [suggestion, setSuggestion] = useState('')
  // Что читается сейчас — с сервера: на новом устройстве это единственный
  // способ узнать, где человек остановился.
  const [reading, setReading] = useState<ShelfBook[]>([])

  // Номер запроса: ответ устаревшего поиска не должен затирать актуальный.
  const ticket = useRef(0)

  useEffect(() => {
    api.appInfo().then(setApp).catch(() => setApp({ available: false }))
    api
      .history({ limit: 3 })
      .then((page) => setReading(page.items))
      .catch(() => {
        /* без подсказки о начатом каталог всё равно работает */
      })
  }, [])

  const load = useCallback(
    async (offset: number) => {
      const mine = ++ticket.current
      setLoading(true)
      try {
        const page = await api.books({
          ...filter,
          q: query,
          readable,
          sort,
          limit: PAGE,
          offset,
        })
        if (mine !== ticket.current) return
        setTotal(page.total)
        setBooks((prev) => (offset === 0 ? page.items : [...prev, ...page.items]))
        setSuggestion(page.suggestion ?? '')
        setError(null)
      } catch (exc) {
        if (mine === ticket.current) setError(describe(exc))
      } finally {
        if (mine === ticket.current) setLoading(false)
      }
    },
    [filter, query, readable, sort],
  )

  // Поиск с задержкой: набор текста не должен порождать запрос на каждую букву.
  useEffect(() => {
    const timer = window.setTimeout(() => void load(0), query ? 250 : 0)
    return () => window.clearTimeout(timer)
  }, [load, query])

  // Одно название и один автор у нескольких книг — это разные издания или
  // переводы. Только для них имеет смысл дочитывать подробности: на обычной
  // странице каталога это были бы сотни лишних чтений файлов.
  useEffect(() => {
    const groups = new Map<string, number[]>()
    for (const book of books) {
      const key = `${book.title}|${book.authors.map((a) => a.name).join(',')}`
      const list = groups.get(key)
      if (list) list.push(book.id)
      else groups.set(key, [book.id])
    }
    const twins = [...groups.values()]
      .filter((ids) => ids.length > 1)
      .flat()
      .filter((id) => !(String(id) in details))
      .slice(0, 200)
    if (twins.length === 0) return

    let cancelled = false
    api
      .booksDetails(twins)
      .then((response) => {
        if (!cancelled) setDetails((prev) => ({ ...prev, ...response.items }))
      })
      .catch(() => {
        /* без подсказки об издании список всё равно работает */
      })
    return () => {
      cancelled = true
    }
  }, [books, details])

  const selectNode = (next: BookFilter, title: string) => {
    setFilter(next)
    setScope(title)
    setQuery('')
    setSelected(null)
    setSelectedId(null)
    setTreeOpen(false)
  }

  const clearScope = () => {
    setFilter({})
    setScope('')
  }

  const openRandom = async () => {
    try {
      const book = await api.randomBook(filter.genre ? { genre: filter.genre } : {})
      navigate(`/book/${book.id}`)
    } catch {
      setError('не удалось выбрать книгу')
    }
  }

  const openBook = async (book: BookSummary) => {
    setSelectedId(book.id)
    setSelected(null)
    try {
      setSelected(await api.book(book.id))
    } catch (exc) {
      setError(describe(exc))
    }
  }

  return (
    <div className="catalog">
      <header className="catalog__bar">
        <div className="catalog__bar-row">
          <button
            className="btn btn--ghost catalog__menu"
            onClick={() => setTreeOpen(true)}
            title="Авторы, серии, жанры"
          >
            ☰
          </button>
          <span className="topbar__brand">Chita</span>
          <span className="topbar__spacer" />
          {app?.available && (
            <a
              className="btn catalog__app"
              href={app.url}
              download
              title={`${app.name}, ${formatSize(app.size ?? 0)}`}
            >
              <span className="catalog__app-full">Приложение для Android</span>
              <span className="catalog__app-short">Android</span>
              <span className="dim"> · {formatSize(app.size ?? 0)}</span>
            </a>
          )}
          <Link className="btn btn--ghost" to="/shelf" title="Полка, история, выписки">
            Полка
          </Link>
          <button
            className="btn btn--ghost"
            title="Случайная книга"
            onClick={() => void openRandom()}
          >
            Наугад
          </button>
          <Link className="btn btn--ghost" to="/status" title="Состояние сервера">
            ⚙
          </Link>
          <span className="dim catalog__user">{username}</span>
          <button className="btn btn--ghost" onClick={() => void logout()}>
            Выйти
          </button>
        </div>
        <div className="catalog__bar-row catalog__bar-row--search">
          <div className="search">
            <input
              type="search"
              value={query}
              placeholder="Название, автор или серия"
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <button
            className={`btn${readable ? ' btn--primary' : ''}`}
            onClick={() => setReadable(!readable)}
            title="Только книги, которые открываются в читалке"
          >
            Читаемые
          </button>
        </div>
      </header>

      <div className="catalog__body">
        <aside className="catalog__side">
          <CatalogTree filter={filter} onSelect={selectNode} />
        </aside>

        {treeOpen && (
          <>
            <div className="overlay" onClick={() => setTreeOpen(false)} />
            <aside className="catalog__drawer">
              <div className="catalog__drawer-head">
                <span>Каталог</span>
                <button className="btn btn--ghost" onClick={() => setTreeOpen(false)}>
                  ×
                </button>
              </div>
              <CatalogTree filter={filter} onSelect={selectNode} />
            </aside>
          </>
        )}

        <main className="catalog__main">
          <div className="catalog__status">
            {scope && (
              <span className="chip">
                {scope}
                <button onClick={clearScope} title="Показать всю библиотеку">
                  ×
                </button>
              </span>
            )}
            <span className="dim">
              {error ? error : loading && books.length === 0 ? 'Загрузка…' : `Книг: ${total}`}
            </span>
            {suggestion && (
              <span className="dim">
                Возможно, вы искали{' '}
                <button className="linkish" onClick={() => setQuery(suggestion)}>
                  {suggestion}
                </button>
              </span>
            )}
          </div>

          {reading.length > 0 && !query && !scope && (
            <div className="continue">
              <span className="continue__label">Продолжить чтение:</span>
              {reading.map((book) => (
                <Link key={book.id} className="continue__item" to={`/read/${book.id}`}>
                  {book.title}
                  <span className="dim"> · {Math.round(book.progress * 100)} %</span>
                </Link>
              ))}
            </div>
          )}

          <BookTable
            books={books}
            details={details}
            total={total}
            loading={loading}
            sort={sort}
            selectedId={selectedId}
            onSort={setSort}
            onOpen={openBook}
            onAuthor={(id, name) => selectNode({ author_id: id }, name)}
            onSeries={(id, name) => selectNode({ series_id: id }, name)}
            onLoadMore={() => void load(books.length)}
          />

          {selectedId !== null && (
            <BookPanel
              book={selected}
              onClose={() => {
                setSelected(null)
                setSelectedId(null)
              }}
              onAuthor={(id, name) => selectNode({ author_id: id }, name)}
              onSeries={(id, name) => selectNode({ series_id: id }, name)}
              onGenre={(code, name) => selectNode({ genre: code }, name)}
            />
          )}
        </main>
      </div>
    </div>
  )
}

/** Карточка выбранной книги под таблицей. */
function BookPanel({
  book,
  onClose,
  onAuthor,
  onSeries,
  onGenre,
}: {
  book: BookDetail | null
  onClose: () => void
  onAuthor: (id: number, name: string) => void
  onSeries: (id: number, name: string) => void
  onGenre: (code: string, name: string) => void
}) {
  if (!book) {
    return (
      <div className="panel-book">
        <Spinner />
      </div>
    )
  }

  const d = book.details
  const rows: [string, React.ReactNode][] = []
  if (book.series) {
    rows.push([
      'Серия',
      <button className="linkish" onClick={() => onSeries(book.series!.id, book.series!.name)}>
        {book.series.name}
        {book.series_index ? ` · ${Math.round(book.series_index)}` : ''}
      </button>,
    ])
  }
  if (book.genres.length) {
    rows.push([
      'Жанры',
      book.genres.map((genre, index) => (
        <span key={genre.code}>
          {index > 0 && ', '}
          <button className="linkish" onClick={() => onGenre(genre.code, genre.name)}>
            {genre.name}
          </button>
        </span>
      )),
    ])
  }
  if (d.translators.length) rows.push(['Перевод', d.translators.join(', ')])
  if (d.src_title) rows.push(['Оригинал', `${d.src_title}${d.src_lang ? ` (${d.src_lang})` : ''}`])
  if (d.publisher || d.publish_city || d.publish_year) {
    rows.push([
      'Издание',
      [d.publisher, d.publish_city, d.publish_year].filter(Boolean).join(', '),
    ])
  }
  if (d.isbn) rows.push(['ISBN', d.isbn])
  if (book.year) rows.push(['Год написания', String(book.year)])
  if (book.lang) rows.push(['Язык', book.lang])
  if (d.keywords.length) rows.push(['Ключевые слова', d.keywords.join(', ')])

  return (
    <div className="panel-book">
      <button className="panel-book__close" onClick={onClose} title="Закрыть">
        ×
      </button>
      <div className="panel-book__cover">
        {book.has_cover ? (
          <img src={coverUrl(book.id)} alt="" />
        ) : (
          <div className="card__placeholder">{book.title}</div>
        )}
      </div>
      <div className="panel-book__info">
        <h2>{book.title}</h2>
        <div className="panel-book__authors">
          {book.authors.map((author, index) => (
            <span key={author.id}>
              {index > 0 && ', '}
              <button className="linkish" onClick={() => onAuthor(author.id, author.name)}>
                {author.name}
              </button>
            </span>
          ))}
        </div>

        <div className="panel-book__actions">
          {book.readable && (
            <Link className="btn btn--primary" to={`/read/${book.id}`}>
              {book.reading && book.reading.progress > 0
                ? `Продолжить · ${Math.round(book.reading.progress * 100)} %`
                : 'Читать'}
            </Link>
          )}
          {book.files.map((file) => (
            <a key={file.id} className="btn" href={downloadUrl(file.id)}>
              {file.format.toUpperCase()}
              <span className="dim"> · {formatSize(file.size)}</span>
            </a>
          ))}
        </div>

        {book.annotation && <p className="panel-book__annotation">{book.annotation}</p>}
        {!book.annotation && !d.filled && (
          <p className="dim">Аннотация в файле не указана.</p>
        )}

        {rows.length > 0 && (
          <dl className="meta">
            {rows.map(([label, value]) => (
              <div key={label} className="meta__row">
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </div>
  )
}

function describe(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message)
  }
  return 'Не удалось загрузить каталог'
}
